import React, { ChangeEvent, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useAsyncFn from 'react-use/lib/useAsyncFn';

import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { Avatar, Content } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import {
  Box,
  Button,
  IconButton,
  TextField,
  makeStyles,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import { Alert, Autocomplete } from '@material-ui/lab';

import { tomtomLabApiRef } from '../../api';
import { useEventTheme } from '../../context/EventThemeContext';
import { useEvents } from '../../hooks/useEvents';
import { useParticipant } from '../../hooks/useParticipant';
import { Idea, IdeaExportData } from '../../types';
import { exportIdeasAsCSV } from '../../util/exportIdeasAsCSV';
import { fetchCatalogItems, fetchIdeaMembers } from '../../util/fetchMethods';
import { fetchUserNames } from '../../util/fetchUserName';
import { mapWithConcurrency } from '../../util/mapWithConcurrency';
import { parseIdea } from '../../util/parseMethods';
import { sortByDate, sortByMembers, sortByTitle } from '../../util/sortMethods';
import { IdeaEventSelector } from '../IdeaEventSelector';
import { IdeaPreview } from '../IdeaPreview/IdeaPreview';
import { IdeaStatusSelector } from '../IdeaStatusSelector';
import { SortMethodSelector } from '../SortMethodSelector';
import buttonImage from '../images/2026-images-main-landing-page/button.png';
import { MatchMakingAlert } from './MatchMakingAlert';
import { NonParticipantAlert } from './NonParticipantAlert';

/**
 * In-flight member requests while building the search index. Roughly the
 * browser's own HTTP/1.1 per-origin cap, low enough that HTTP/2 does not reset
 * streams on events with hundreds of ideas.
 */
const MEMBER_REQUEST_CONCURRENCY = 6;

const useStyles = makeStyles<{}, { cosmic: boolean }>(() => ({
  button: { minWidth: '11rem' },
  container: { marginTop: '2rem' },
  header: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'left',
    alignItems: 'center',
    margin: '0 auto',
    marginBottom: '1.2rem',
    color: ({ cosmic }) => (cosmic ? '#ffffff' : undefined),
    '& .MuiTypography-root': {
      color: ({ cosmic }: { cosmic: boolean }) =>
        cosmic ? '#ffffff' : undefined,
    },
    '& .MuiFormLabel-root': {
      color: ({ cosmic }: { cosmic: boolean }) =>
        cosmic ? 'rgba(255,255,255,0.7)' : undefined,
    },
    '& .MuiInputBase-root': {
      color: ({ cosmic }: { cosmic: boolean }) =>
        cosmic ? '#ffffff' : undefined,
    },
    '& .MuiInputBase-input': {
      color: ({ cosmic }: { cosmic: boolean }) =>
        cosmic ? '#ffffff' : undefined,
    },
    '& .MuiSelect-icon': {
      color: ({ cosmic }: { cosmic: boolean }) =>
        cosmic ? 'rgba(255,255,255,0.7)' : undefined,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: ({ cosmic }: { cosmic: boolean }) =>
        cosmic ? 'rgba(255,255,255,0.3)' : undefined,
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: ({ cosmic }: { cosmic: boolean }) =>
        cosmic ? 'rgba(255,255,255,0.3)' : undefined,
    },
    '& .MuiAutocomplete-popupIndicator': {
      color: ({ cosmic }: { cosmic: boolean }) =>
        cosmic ? 'rgba(255,255,255,0.7)' : undefined,
    },
    '& .MuiAutocomplete-clearIndicator': {
      color: ({ cosmic }: { cosmic: boolean }) =>
        cosmic ? 'rgba(255,255,255,0.7)' : undefined,
    },
  },
  search: { height: '2.5rem', width: '30rem' },
  pullRight: { marginLeft: 'auto' },
  eventFilterContainer: { display: 'flex', width: '100%' },
  formLabel: { display: 'flex', alignItems: 'center', marginRight: '5px' },
  actionButton: {
    backgroundImage: `url(${buttonImage})`,
    backgroundSize: '100% 100%',
    backgroundRepeat: 'no-repeat',
    color: '#000000',
    fontFamily: 'Carbon, sans-serif',
    fontSize: '1rem',
    fontWeight: 100,
    letterSpacing: '0.1em',
    textTransform: 'none',
    padding: '0 !important',
    height: 38,
    width: 160,
    minWidth: 'unset',
    '&:hover': {
      backgroundImage: `url(${buttonImage})`,
      backgroundSize: '100% 100%',
    },
  },
}));

/** @public */
export type SortViewProps = {
  showRegisterIdeaButton: boolean;
  fullWidth?: boolean;
  fullHeight?: boolean;
};

/** @public */
export const SortView = (props: SortViewProps) => {
  const { fullWidth = true, fullHeight = true, showRegisterIdeaButton } = props;
  const tomtomLabApi = useApi(tomtomLabApiRef);
  const catalogApi = useApi(catalogApiRef);
  const { isCosmicTheme: cosmic } = useEventTheme();
  const classes = useStyles({ cosmic });
  const [searchParams, setSearchParams] = useSearchParams();
  const sortMethods = [sortByDate, sortByTitle, sortByMembers];
  const statuses = [
    'all',
    'draft',
    'readyForHacking',
    'readyForJury',
    'readyForFinals',
    'doneHacking',
    'canceled',
  ];

  const [selectedEventId, setSelectedEventId] = useState(() => {
    const v = parseInt(searchParams.get('event') ?? '', 10);
    return Number.isNaN(v) || v < 0 ? 0 : v;
  });
  const [sortMethodNbr, setSortMethodNbr] = useState(() => {
    const v = parseInt(searchParams.get('sort') ?? '', 10);
    return Number.isNaN(v) || v < 0 || v >= sortMethods.length ? 0 : v;
  });
  const [ideaStatusNumber, setIdeaStatusNumber] = useState(() => {
    const v = parseInt(searchParams.get('status') ?? '', 10);
    return Number.isNaN(v) || v < 0 || v >= statuses.length ? 0 : v;
  });
  const [searchValue, setSearchValue] = useState(
    () => searchParams.get('q') ?? '',
  );
  const [memberNames, setMemberNames] = useState<Map<number, string>>(
    new Map(),
  );
  const [personNames, setPersonNames] = useState<
    { name: string; picture?: string }[]
  >([]);

  const updateSearchParams = (patch: Record<string, string | null>) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        Object.entries(patch).forEach(([k, v]) => {
          if (v === null || v === '0' || v === '') next.delete(k);
          else next.set(k, v);
        });
        return next;
      },
      { replace: true },
    );
  };

  const [catalogEntities, fetchCatalogEntities] = useAsyncFn(async () => {
    return await fetchCatalogItems(catalogApi);
  });

  const [ideas, fetchIdeas] = useAsyncFn(async (eventId: number) => {
    const limit = undefined;
    const order = undefined;
    const response = await tomtomLabApi.getIdeas(limit, order, eventId);
    const ideaList: Idea[] = [];

    response.data.forEach((idea: any) => {
      ideaList.push(parseIdea(idea));
    });

    return ideaList;
  });

  const { isUserParticipant } = useParticipant();

  const { events } = useEvents({ order: 'latest' });

  useEffect(() => {
    if (events && events.length > 0 && !searchParams.get('event')) {
      const activeId = events.find(event => event.isActive)?.id ?? 0;
      setSelectedEventId(activeId);
    }
  }, [events]); // eslint-disable-line react-hooks/exhaustive-deps

  const catalogEntityRefs = catalogEntities.value?.map((project: Entity) =>
    stringifyEntityRef(project),
  );

  const getSearchResults = () => {
    const query = searchValue.toLocaleLowerCase();
    const searchedIdeas = ideas.value
      ?.filter(
        idea =>
          idea.title.toLocaleLowerCase().includes(query) ||
          (idea.summary ?? '').toLocaleLowerCase().includes(query) ||
          (idea.id !== undefined &&
            (memberNames.get(idea.id) ?? '').includes(query)),
      )
      .sort(sortMethods[sortMethodNbr]);
    if (ideaStatusNumber) {
      return searchedIdeas?.filter(
        idea => idea.status === statuses[ideaStatusNumber],
      );
    }
    return searchedIdeas;
  };

  useEffect(() => {
    const filterBrokenLinks = () => {
      if (catalogEntityRefs) {
        ideas.value?.forEach(async (project: Idea) => {
          if (project.entityRef) {
            if (!catalogEntityRefs?.includes(project.entityRef)) {
              await tomtomLabApi.updateIdea({
                ...project,
                entityRef: null,
              });
            }
          }
        });
      }
    };
    filterBrokenLinks();
  }, [tomtomLabApi, ideas.value, catalogEntityRefs, catalogEntities.value]);

  useEffect(() => {
    fetchCatalogEntities();
    fetchIdeas(selectedEventId);
  }, [fetchIdeas, fetchCatalogEntities, selectedEventId]);

  useEffect(() => {
    if (!ideas.value?.length) return;
    const buildIndex = async () => {
      const index = new Map<number, string>();
      const uniquePersons = new Map<
        string,
        { name: string; picture?: string }
      >();

      const indexedIdeas = ideas.value!.filter(idea => Boolean(idea.id));

      // One member request per idea, capped, then a single batched catalog
      // resolution for every ref on the screen. Doing both per idea inside a
      // Promise.all opened hundreds of concurrent requests on large events.
      const membersByIdea = await mapWithConcurrency(
        indexedIdeas,
        MEMBER_REQUEST_CONCURRENCY,
        idea => fetchIdeaMembers(tomtomLabApi, idea),
      );

      const nameByRef = await fetchUserNames(
        [
          ...indexedIdeas.map(idea => idea.responsible),
          ...membersByIdea.flat().map(member => member.userRef),
        ],
        catalogApi,
      );

      indexedIdeas.forEach((idea, ideaIndex) => {
        const names: string[] = [];
        const responsibleName = idea.responsible
          ? nameByRef.get(idea.responsible) ?? ''
          : '';
        if (responsibleName) {
          names.push(responsibleName);
          if (!uniquePersons.has(responsibleName))
            uniquePersons.set(responsibleName, { name: responsibleName });
        }
        for (const m of membersByIdea[ideaIndex]) {
          const name = m.userRef ? nameByRef.get(m.userRef) ?? '' : '';
          if (name) {
            names.push(name);
            if (!uniquePersons.has(name))
              uniquePersons.set(name, { name, picture: m.picture });
          }
        }
        index.set(idea.id!, names.join(' ').toLocaleLowerCase());
      });

      setMemberNames(index);
      setPersonNames(
        Array.from(uniquePersons.values()).sort((a, b) =>
          a.name.localeCompare(b.name),
        ),
      );
    };
    buildIndex();
  }, [ideas.value, tomtomLabApi, catalogApi]);

  const handleSortMethodChange = (
    event: ChangeEvent<{ name?: string | undefined; value: unknown }>,
  ) => {
    const val = typeof event.target.value === 'number' ? event.target.value : 0;
    setSortMethodNbr(val);
    updateSearchParams({ sort: String(val) });
  };

  const handleSelectedStatusChange = (
    event: ChangeEvent<{ name?: string | undefined; value: unknown }>,
  ) => {
    const val = typeof event.target.value === 'number' ? event.target.value : 0;
    setIdeaStatusNumber(val);
    updateSearchParams({ status: String(val) });
  };

  if (catalogEntities.error)
    return <Alert severity="error">{catalogEntities.error.message}</Alert>;

  if (ideas.error) return <Alert severity="error">{ideas.error.message}</Alert>;

  const exportIdeas = async () => {
    const ideasToExport = getSearchResults();
    const eventTitles = await Promise.all(
      (ideasToExport ?? []).map(idea =>
        tomtomLabApi
          .getEventById(idea.eventId ?? 0)
          .then(event => event?.title),
      ),
    );

    const ideasMembers: any[] = await Promise.all(
      (ideasToExport ?? []).map(idea =>
        fetchIdeaMembers(tomtomLabApi, idea).then(member => member),
      ),
    );

    const data: IdeaExportData[] = (ideasToExport ?? []).map((idea, i) => {
      const eventTitle = eventTitles[i] ?? '';
      const members = ideasMembers[i];

      const cleanUpUserRef = (userRef: string | undefined) =>
        (userRef ?? '')
          .replace('user:default/', '')
          .replace('_tomtom.com', '@tomtom.com');

      return {
        title: idea.title,
        teaser: idea.summary,
        event: eventTitle,
        summary: idea.summary,
        status: idea.status,
        date: idea.updatedAt ?? '',
        responsible: cleanUpUserRef(idea.responsible),
        members: members
          ?.filter((m: any) => m.userRef)
          ?.map((m: any) => cleanUpUserRef(m.userRef))
          ?.join(' '),
      };
    }) as IdeaExportData[];

    exportIdeasAsCSV(data, `ideas_${Date.now().toString()}`);
  };

  return (
    <Content noPadding>
      {!isUserParticipant && <NonParticipantAlert />}
      {isUserParticipant && <MatchMakingAlert />}
      <Box className={classes.header} width="100%">
        <Box display="flex" width="100%" justifyContent="space-between">
          <Box
            display="flex"
            flexBasis="80%"
            justifyContent="space-between"
            gridGap="0px"
          >
            <Box display="flex">
              <IdeaEventSelector
                events={events ?? []}
                selectedEventId={selectedEventId}
                onSelect={(eventId: number): void => {
                  setSelectedEventId(eventId);
                  fetchIdeas(eventId);
                  updateSearchParams({ event: String(eventId) });
                }}
              />
            </Box>

            <Box display="flex">
              <IdeaStatusSelector
                statusNumber={ideaStatusNumber}
                handleSelectedStatusChange={handleSelectedStatusChange}
              />
            </Box>

            <Box display="flex">
              <SortMethodSelector
                sortMethodNbr={sortMethodNbr}
                handleSortMethodChange={handleSortMethodChange}
              />
            </Box>

            <Autocomplete
              freeSolo
              options={personNames}
              inputValue={searchValue}
              getOptionLabel={option =>
                typeof option === 'string' ? option : option.name
              }
              filterOptions={(options, state) =>
                state.inputValue.length >= 3
                  ? options.filter(o =>
                      o.name
                        .toLocaleLowerCase()
                        .includes(state.inputValue.toLocaleLowerCase()),
                    )
                  : []
              }
              onInputChange={(_e, newValue, reason) => {
                if (reason === 'reset' && newValue === '') return;
                setSearchValue(newValue);
                updateSearchParams({ q: newValue });
              }}
              onChange={(_e, selected) => {
                let val = '';
                if (selected) {
                  val = typeof selected === 'string' ? selected : selected.name;
                }
                setSearchValue(val);
                updateSearchParams({ q: val });
              }}
              renderOption={option => (
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <Avatar
                    displayName={option.name}
                    picture={option.picture}
                    customStyles={{ width: 24, height: 24, fontSize: 10 }}
                  />
                  {option.name}
                </Box>
              )}
              className={classes.search}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="Search titles or people…"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    style: { height: '2.5rem', padding: '0 8px' },
                  }}
                />
              )}
            />
          </Box>

          <Box display="flex" alignItems="center" gridGap="10px">
            {cosmic ? (
              <Button
                className={classes.actionButton}
                component={Link}
                to="/tomtom-lab/register-idea"
                style={{ display: showRegisterIdeaButton ? 'inherit' : 'none' }}
              >
                Register idea
              </Button>
            ) : (
              <Button
                className={classes.button}
                variant="contained"
                color="primary"
                component={Link}
                to="/tomtom-lab/register-idea"
                style={{ display: showRegisterIdeaButton ? 'inherit' : 'none' }}
              >
                Register idea
              </Button>
            )}
            {ideas.value && ideas.value.length > 0 && (
              <IconButton
                color={cosmic ? 'default' : 'primary'}
                style={cosmic ? { color: 'rgba(255,255,255,0.5)' } : undefined}
                onClick={async () => await exportIdeas()}
              >
                <GetAppIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      <IdeaPreview
        ideas={getSearchResults() || []}
        fetchIdeas={() => fetchIdeas(selectedEventId)}
        gridSize={fullWidth ? 2 : 4}
        height={fullHeight ? 'large' : 'small'}
      />
      <Content noPadding className={classes.container} />
    </Content>
  );
};
